import { useState, useEffect, useRef, type FormEvent, type ChangeEvent } from "react";
import type { NotificationKind } from "@/providers/NotificationProvider";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { fieldLabelClass, textareaClass, inputClass } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { useApiClient } from "@/providers/ApiClientProvider";
import { useNotification } from "@/providers/NotificationProvider";
import { notifications, ui, placeholders, charactersWithMin } from "@/strings";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type SubmitState = "idle" | "uploading" | "submitting" | "upload-failed";

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const api = useApiClient();
  const { show } = useNotification();
  // Avoid lint errors for notification kinds by using typed constants
  const TOAST_ERROR: NotificationKind = "error";
  const TOAST_SUCCESS: NotificationKind = "success";
  const TOAST_WARNING: NotificationKind = "warning";

  const [description, setDescription] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Use ref to track preview URLs for cleanup without causing re-renders
  const previewUrlsRef = useRef<string[]>([]);
  previewUrlsRef.current = previewUrls;

  const isSubmitting = submitState === "uploading" || submitState === "submitting";

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setDescription("");
      setSelectedFiles([]);
      setSubmitState("idle");
      setUploadError(null);
      // Revoke all preview URLs to prevent memory leaks (use ref to avoid stale closure)
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      setPreviewUrls([]);
    }
  }, [isOpen]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Filter only image files
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length !== files.length) {
      show(notifications.feedback.onlyImagesAllowed, TOAST_ERROR);
    }

    // Limit to 3 files
    if (selectedFiles.length + imageFiles.length > 3) {
      show(notifications.feedback.maxImagesExceeded, TOAST_ERROR);
      return;
    }

    // Create object URLs for new files
    const newUrls = imageFiles.map((file) => URL.createObjectURL(file));
    setSelectedFiles([...selectedFiles, ...imageFiles]);
    setPreviewUrls([...previewUrls, ...newUrls]);
  };

  const removeFile = (index: number) => {
    // Revoke the object URL for the removed file
    URL.revokeObjectURL(previewUrls[index]);
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    setPreviewUrls(previewUrls.filter((_, i) => i !== index));
  };

  // Collect browser context with defensive checks for SSR/testing environments
  const getContext = () => ({
    pageUrl: typeof window !== "undefined" ? window.location.href : null,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    browserInfo:
      typeof window !== "undefined" && typeof screen !== "undefined"
        ? {
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
          }
        : null,
  });

  const submitFeedbackOnly = async () => {
    setSubmitState("submitting");
    try {
      await api.submitFeedback({
        description: description.trim(),
        ...getContext(),
      });
      show(notifications.feedback.submittedWithoutFiles, TOAST_SUCCESS);
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      show(notifications.feedback.submitFailed, TOAST_ERROR);
      setSubmitState("idle");
    }
  };

  const handleSubmitWithoutFiles = () => {
    setUploadError(null);
    // Revoke all preview URLs when clearing files
    previewUrls.forEach((url) => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setPreviewUrls([]);
    void submitFeedbackOnly();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (description.trim().length < 10) {
      show(notifications.feedback.minLengthRequired, TOAST_ERROR);
      return;
    }

    // If there are files, try to upload them first (via a temporary feedback entry)
    if (selectedFiles.length > 0) {
      setSubmitState("uploading");
      setUploadError(null);

      try {
        // Create feedback first (we need the ID for attachments)
        const feedback = await api.submitFeedback({
          description: description.trim(),
          ...getContext(),
        });

        // Try to upload all attachments
        const uploadResults = await Promise.allSettled(
          selectedFiles.map((file) => api.uploadFeedbackAttachment(feedback.id, file))
        );

        const failedUploads = uploadResults.filter((r) => r.status === "rejected");

        if (failedUploads.length > 0) {
          // Log upload failures for debugging
          failedUploads.forEach((result) => {
            if (result.status === "rejected") {
              console.error("Failed to upload feedback attachment:", result.reason);
            }
          });

          // Some uploads failed - show warning since feedback was still submitted
          if (failedUploads.length === selectedFiles.length) {
            show(notifications.feedback.allUploadsFailedButSubmitted, TOAST_WARNING);
          } else {
            show(notifications.feedback.someUploadsFailedButSubmitted(failedUploads.length, selectedFiles.length), TOAST_WARNING);
          }
        } else {
          show(notifications.feedback.submitted, TOAST_SUCCESS);
        }

        setTimeout(() => onClose(), 1500);
      } catch (error) {
        // Feedback submission itself failed - show error with option to retry without files
        console.error("Failed to submit feedback:", error);
        setUploadError(notifications.feedback.uploadFailed);
        setSubmitState("upload-failed");
      }
    } else {
      // No files, just submit feedback
      await submitFeedbackOnly();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalHeader title={ui.headers.feedback} onClose={onClose} />
      <ModalBody>
        <form onSubmit={(e) => void handleSubmit(e)} id="feedback-form" className="space-y-4">
          <p className="text-sm text-text-theme-secondary">{ui.labels.feedbackIntro}</p>

          <label className={fieldLabelClass}>
            {ui.labels.feedbackQuestion}
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={textareaClass}
              rows={6}
              required
              minLength={10}
              placeholder={placeholders.feedback.message}
              autoFocus
              disabled={isSubmitting}
            />
            <p className="mt-1 text-xs text-text-theme-muted">{charactersWithMin(description.length, 10)}</p>
          </label>

          <label className={fieldLabelClass}>
            {ui.labels.screenshotsOptional}
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className={inputClass}
              disabled={isSubmitting || selectedFiles.length >= 3}
            />
            <p className="mt-1 text-xs text-text-theme-muted">{ui.labels.feedbackUploadHint}</p>
          </label>

          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-text-theme-primary">{ui.labels.selectedImages}</p>
              <div className="flex flex-wrap gap-2">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={previewUrls[index]}
                      alt={file.name}
                      className="h-20 w-20 rounded-lg border border-border object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-semantic-error text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      disabled={isSubmitting}
                    >
                      {/* eslint-disable-next-line no-restricted-syntax */}
                      <span aria-hidden="true">×</span>
                    </button>
                    <p className="mt-1 text-xs text-text-theme-muted truncate w-20">{file.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {uploadError && submitState === "upload-failed" && (
            <div className="rounded-lg border border-warning bg-surface-warning p-3">
              <p className="text-sm text-text-warning">{uploadError}</p>
            </div>
          )}
        </form>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          className={buttonVariants.secondary}
          disabled={isSubmitting}
        >
          {ui.actions.cancel}
        </button>
        {submitState === "upload-failed" ? (
          <button
            type="button"
            onClick={handleSubmitWithoutFiles}
            className={buttonVariants.primary}
            disabled={description.trim().length < 10}
          >
            {ui.actions.submitWithoutScreenshots}
          </button>
        ) : (
          <button
            type="submit"
            form="feedback-form"
            className={buttonVariants.primary}
            disabled={isSubmitting || description.trim().length < 10}
          >
            {isSubmitting ? ui.actions.submitting : ui.actions.submit}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}
